import React, {FC} from 'react';

import classNames from 'classnames';
import {connect} from 'react-redux';

import {ApplicationStoreType} from 'reducers';
import {getActiveCartItems} from 'selectors/cart/cart';
import {QuoteItemType} from 'typescript/cartTypes';

import s from './cartSkeleton.scss';

type PropsType = {
    cartItems: QuoteItemType[];
};

type PlaceholderPropsType = {
    className?: string;
    tag?: 'div' | 'li';
    ariaLabel?: string;
};

export const CartSkeletonAriaNames = {
    cartItemPlaceholder: 'cart item placeholder',
};

/**
 * Returns a skeleton of cart page
 * @param {QuoteItemType} - cart item
 * @returns {React.ReactElement} - skeleton
 */
export const CartSkeleton: FC<PropsType> = ({cartItems}) => {
    const isCartEmpty = !cartItems?.length;

    const Placeholder: FC<PlaceholderPropsType> = React.useCallback(
        ({className, tag = 'div', ariaLabel}) =>
            React.createElement(tag, {
                className: classNames(className, s.placeholder),
                'aria-label': ariaLabel,
            }),
        [],
    );

    if (isCartEmpty) {
        return (
            <div className={s.emptyCartContainer}>
                <div className={s.emptyCartBlock}>
                    <Placeholder className={s.emptyCartIcon} />
                    <Placeholder className={s.emptyCartTitle} />
                    <Placeholder className={s.emptyCartSubtitle} />
                    <div className={s.emptyCartButtons}>
                        <Placeholder className={s.emptyCartButton} />
                        <Placeholder className={s.emptyCartButton} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={s.cartWithItemsContainer}>
            <Placeholder className={s.title} />
            <Placeholder className={s.subtitle} />
            <Placeholder className={s.mainBlock} />
            <Placeholder className={s.proceedToCheckoutButton} />
            <div className={s.paymentButtons}>
                <Placeholder className={s.checkoutButton} />
                <Placeholder className={s.checkoutButton} />
            </div>
            <div className={s.delimeter} />
            <ul className={s.list}>
                {cartItems.map(item => (
                    <Placeholder
                        tag="li"
                        ariaLabel={CartSkeletonAriaNames.cartItemPlaceholder}
                        className={
                            item.type === 'contact_lenses'
                                ? s.cartItemLensesPlaceholder
                                : s.cartItemGlassesPlaceholder
                        }
                        key={item.id}
                    />
                ))}
            </ul>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (state: ApplicationStoreType) => ({
    cartItems: getActiveCartItems(state),
});

export const CartSkeletonConnected = connect(mapStateToProps)(CartSkeleton);
